import type { ReactNode } from 'react';
import { garmentImage, garmentOf, isDarkColor, type GarmentType } from '@/shared/content/garments';
import styles from './ls-garment-pair.module.scss';

interface Props {
  type: GarmentType;
  color: string;
  /** Что лежит в зоне печати спереди. */
  front?: ReactNode;
  /** То же сзади. Пусто — спина чистая. */
  back?: ReactNode;
  frontLabel?: string;
  backLabel?: string;
  /**
   * Показать одну сторону. Нужно там, где для двух нет места:
   * на последнем экране и на телефоне.
   */
  only?: 'front' | 'back';
  /** Убрать подписи сторон: на плашке они лишние. */
  isBare?: boolean;
}

/**
 * Изделие с двух сторон разом.
 *
 * Показывать перёд и спину по очереди переключателем здесь неправильно:
 * человек собирает принт и должен видеть обе стороны одновременно —
 * иначе он не заметит, что сзади пусто, пока не дойдёт до заказа.
 *
 * Зона печати задана долями из реестра изделий — теми же, по которым
 * считает конструктор и серверная сборка мокапа. Любое своё число здесь
 * увело бы принт с футболки при первой же смене изделия.
 */
export function LsGarmentPair({
  type,
  color,
  front,
  back,
  frontLabel,
  backLabel,
  only,
  isBare,
}: Props) {
  const garment = garmentOf(type);
  const zone = garment.zone;

  // Принту нужно знать, на чём он лежит: подписи «EST.» и год читаются
  // по ткани, а не по цвету имени. Сообщаем это признаком на зоне —
  // так знание о цвете остаётся здесь, где цвет и известен.
  const tone = isDarkColor(color) ? 'dark' : 'light';

  const area = {
    top: `${zone.y * 100}%`,
    left: `${zone.x * 100}%`,
    width: `${zone.width * 100}%`,
    height: `${zone.height * 100}%`,
  };

  return (
    <div className={styles['ls-garment-pair']}>
      {only === 'back' ? null : (
        <figure className={styles['ls-garment-pair__side']}>
          {!isBare && (
            <figcaption className={styles['ls-garment-pair__label']}>
              {frontLabel ?? 'спереди'}
            </figcaption>
          )}

          <span className={styles['ls-garment-pair__scene']}>
            <img
              className={styles['ls-garment-pair__base']}
              src={garmentImage(garment, color, 'front')}
              alt=""
              aria-hidden="true"
              draggable={false}
            />
            <span className={styles['ls-garment-pair__zone']} style={area} data-tone={tone}>
              {front}
            </span>
          </span>
        </figure>
      )}

      {only === 'front' ? null : (
        <figure className={styles['ls-garment-pair__side']}>
          {!isBare && (
            <figcaption className={styles['ls-garment-pair__label']}>
              {backLabel ?? 'сзади'}
            </figcaption>
          )}

          <span className={styles['ls-garment-pair__scene']}>
            <img
              className={styles['ls-garment-pair__base']}
              src={garmentImage(garment, color, 'back')}
              alt=""
              aria-hidden="true"
              draggable={false}
            />
            <span className={styles['ls-garment-pair__zone']} style={area} data-tone={tone}>
              {back}
            </span>
          </span>
        </figure>
      )}
    </div>
  );
}
